import type {
  QueryOptions,
  MutationOptions,
  OperationVariables,
  DefaultContext,
  ApolloError,
} from '@apollo/client';
import { brandAndCountryFromZone } from '@seek/audience-zones';
import {
  createClient,
  createMockClient,
  type ClientOptions,
} from '@seek/ca-graphql-client';
import { browserHttpLink } from '@seek/ca-graphql-client/browser';
import { nodeHttpLink } from '@seek/ca-graphql-client/node';

import { CANDIDATE_GRAPH_DARK_PROD_URL } from 'src/config';
import { requestMetrics } from 'src/modules/request-metrics';
import type { ApiClientConfig } from 'src/types';

import { mockOverrides as localMockOverrides } from '../api-client/mocks/local/mockOverrides';
import { resolverOverrides as localResolverOverrides } from '../api-client/mocks/local/resolverOverrides';
import { mockOverrides as testMockOverrides } from '../api-client/mocks/test/mockOverrides';
import { resolverOverrides as testResolverOverrides } from '../api-client/mocks/test/resolverOverrides';
import { getOperationName } from '../apollo';
import { AutosuggestTimingLink, OmitTypenameLink } from '../apollo/links';
import { handleError as handleErrorFn } from '../error-handling/error-handling';
import type { BrowserLogExtras } from '../error-logger/browserLog';
import { getQueryParams } from '../utils';

export const apiClient = (apiClientConfig: ApiClientConfig) => {
  const { seekEnv, clientEnv, language, zone } = apiClientConfig;

  const { testData, darkProd } = getQueryParams(
    typeof window !== 'undefined' ? window.location.href : '',
  );
  const darkProdEnv = darkProd !== 'master' ? `?branch=${darkProd}` : '';
  const [brand, country] = brandAndCountryFromZone(zone) || ['SEEK', 'AU'];

  const gqlClient =
    (process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'test' ||
      process.env.CYPRESS_TEST) &&
    (seekEnv === 'local' || seekEnv === 'test')
      ? createMockClient({
          mocks:
            seekEnv === 'test' || testData
              ? testMockOverrides
              : localMockOverrides,
          resolvers:
            seekEnv === 'test' || testData
              ? testResolverOverrides
              : localResolverOverrides,
          links: [OmitTypenameLink, AutosuggestTimingLink],
        })
      : createClient({
          brand: brand.toLowerCase() as ClientOptions['brand'],
          country: country as ClientOptions['country'],
          appName: 'Profile web UI',
          ...(darkProd
            ? {
                graphqlEndpointOverride: `${CANDIDATE_GRAPH_DARK_PROD_URL}${darkProdEnv}`,
              }
            : {}),
          httpLink: clientEnv === 'server' ? nodeHttpLink : browserHttpLink,
          links: [OmitTypenameLink, AutosuggestTimingLink],
          headers: {},
          authOptions: {
            useAuth: true,
            forceStrategy: 'AUTH0',
          },
          batchRequests: true,
          batchRequestOptions: {
            batchMax: 30,
          },
          metricsHandler: requestMetrics,
          cacheOptions: {
            typePolicies: {
              RightToWorkType2: {
                keyFields: ['id', 'formattedDescription'],
              },
              RoleFunction: {
                keyFields: ['id', 'subFunction'],
              },
              Candidate: {
                fields: {
                  preferredLocations2: {
                    merge: false,
                  },
                  skills: {
                    merge: false,
                  },
                },
              },
            },
          },
        });

  const handleError = (
    error: ApolloError,
    browserLogExtras: BrowserLogExtras,
  ) => handleErrorFn(error, zone, language, true, browserLogExtras);

  /** A thin wrapper for the apollo client's `query` method that includes
   * some error handling. This function should be used instead of using the
   * apollo client directly.
   */
  const queryGql = <T = any>(options: QueryOptions<OperationVariables, T>) =>
    gqlClient.query(options).catch((error) => {
      handleError(error, {
        kind: 'GraphQueryError',
        name: getOperationName(options.query),
        input: options.variables || {},
      });
      throw error;
    });

  /** A thin wrapper for the apollo client's `mutate` method that includes
   * some error handling. This function should be used instead of using the
   * apollo client directly.
   */
  const mutateGql = <TData = any>(
    options: MutationOptions<TData, OperationVariables, DefaultContext>,
  ) =>
    gqlClient.mutate(options).catch((error) => {
      handleError(error, {
        kind: 'GraphMutationError',
        name: getOperationName(options.mutation),
        input: options.variables || {},
      });
      throw error;
    });

  return {
    apolloClient: gqlClient,
    queryGql,
    mutateGql,
  };
};
